import React, { useContext } from 'react';
import { ReloadOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  ConfigProvider,
  Divider,
  Flex,
  Row,
  Skeleton,
  Typography,
} from 'antd';

import AccountHeader from '@totem/components/accountPlan/AccountHeader';
import AccountPlanContext from '@totem/components/accountPlan/AccountPlanContext';
import AccountPlanEditor from '@totem/components/accountPlan/AccountPlanEditor';
import ActionPlanCard from '@totem/components/accountPlan/ActionPlanCard';
import HealthCard from '@totem/components/accountPlan/HealthCard';
import KpiBand from '@totem/components/accountPlan/KpiBand';
import OperationalCard from '@totem/components/accountPlan/OperationalCard';
import OpportunitiesCard from '@totem/components/accountPlan/OpportunitiesCard';
import PipelineCard from '@totem/components/accountPlan/PipelineCard';
import QbrHubCard from '@totem/components/accountPlan/QbrHubCard';
import RevenueCard from '@totem/components/accountPlan/RevenueCard';
import RevenueViewCard from '@totem/components/accountPlan/RevenueViewCard';
import StakeholdersCard from '@totem/components/accountPlan/StakeholdersCard';
import StrategyCard from '@totem/components/accountPlan/StrategyCard';
import {
  LiveTag,
  ManualTag,
  SectionHeading,
} from '@totem/components/accountPlan/utilities';
import colors from '@totem/styles/colors';

import './accountPlan.css';

// Navy table headers with white text, matching the plan's section headers.
const tableTheme = {
  components: {
    Table: {
      headerBg: colors.branding.primaryDark,
      headerColor: '#ffffff',
      headerSortActiveBg: colors.branding.primaryDark,
      headerSortHoverBg: colors.branding.primaryDark,
      headerSplitColor: 'rgba(255, 255, 255, 0.2)',
      headerBorderRadius: 0,
    },
  },
};

export const AccountPlanExtra = () => {
  const { loading, onRefresh } = useContext(AccountPlanContext);

  return (
    <Button icon={<ReloadOutlined />} loading={loading} onClick={onRefresh}>
      Refresh
    </Button>
  );
};

const AccountPlanLayout = () => {
  const { data, loading } = useContext(AccountPlanContext);

  if (!data) {
    return <Skeleton active loading={loading} paragraph={{ rows: 12 }} />;
  }

  return (
    <ConfigProvider theme={tableTheme}>
      <div styleName="plan">
        <AccountHeader />
        <Divider style={{ margin: '16px 0' }} />
        <KpiBand />

        <Flex gap={20} wrap align="center" style={{ marginTop: 16 }}>
          <span>
            <LiveTag />{' '}
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              = auto-synced from the IB Portal (read-only)
            </Typography.Text>
          </span>
          <span>
            <ManualTag />{' '}
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              = editable strategy &amp; relationship data
            </Typography.Text>
          </span>
        </Flex>

        <SectionHeading>
          Operational truth — synced from the Portal
        </SectionHeading>
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={12}>
            <OperationalCard />
          </Col>
          <Col xs={24} xl={12}>
            <RevenueCard />
          </Col>
          <Col span={24}>
            <RevenueViewCard />
          </Col>
          <Col span={24}>
            <HealthCard />
          </Col>
          <Col span={24}>
            <PipelineCard />
          </Col>
        </Row>

        <SectionHeading>
          Quarterly Business Review — bring the account story into one place
        </SectionHeading>
        <QbrHubCard />

        <SectionHeading>
          Account strategy &amp; relationships — maintained by the account team
        </SectionHeading>
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={12}>
            <StrategyCard />
          </Col>
          <Col xs={24} xl={12}>
            <StakeholdersCard />
          </Col>
          <Col span={24}>
            <OpportunitiesCard />
          </Col>
          <Col span={24}>
            <ActionPlanCard />
          </Col>
        </Row>

        <Typography.Paragraph
          type="secondary"
          style={{ marginTop: 16, fontSize: 12 }}
        >
          Internal — IB account team only. Live sections come from the IB Portal
          as of the time shown above; the health score is an illustrative
          weighted model.
          {data.plan.updatedBy &&
            ` Plan last updated by ${data.plan.updatedBy}.`}
        </Typography.Paragraph>

        <AccountPlanEditor />
      </div>
    </ConfigProvider>
  );
};

export default AccountPlanLayout;
